/**
 * Maps Firebase Auth errors from signInWithEmailAndPassword to user-facing copy.
 * Invalid email/password use one message so we do not hint whether an account exists.
 *
 * Handles both JS SDK shapes (code auth/…) and Identity Toolkit REST-style payloads
 * (message INVALID_LOGIN_CREDENTIALS, nested error JSON, or HTTP 400).
 */
function extractApiErrorMessage(err) {
  if (!err || typeof err !== 'object') return '';
  if (err.error && typeof err.error === 'object') {
    const inner = err.error;
    if (typeof inner.message === 'string') return inner.message;
    if (Array.isArray(inner.errors) && inner.errors[0] && typeof inner.errors[0].message === 'string') {
      return inner.errors[0].message;
    }
  }
  if (typeof err.message === 'string' && err.message.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(err.message);
      const nested = parsed.error;
      if (nested && typeof nested.message === 'string') return nested.message;
      if (nested && Array.isArray(nested.errors) && nested.errors[0] && typeof nested.errors[0].message === 'string') {
        return nested.errors[0].message;
      }
    } catch (_) {}
  }
  return typeof err.message === 'string' ? err.message : '';
}

function isInvalidLoginCredentials(err) {
  const codeStr = err && err.code != null ? String(err.code) : '';
  const nestedCode = err && err.error && err.error.code != null ? err.error.code : null;
  const msg = extractApiErrorMessage(err) || (err && typeof err.message === 'string' ? err.message : '');
  const upper = msg.toUpperCase();
  if (upper.includes('INVALID_LOGIN_CREDENTIALS')) return true;
  if (codeStr === '400' || nestedCode === 400) {
    if (upper.includes('INVALID_LOGIN') || upper.includes('CREDENTIAL')) return true;
  }
  switch (codeStr) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
      return true;
    default:
      return false;
  }
}

export function mapFirebaseSignInError(err) {
  if (isInvalidLoginCredentials(err)) {
    return 'We could not sign you in with those details. Check your email and password are correct, or use Forgot password below.';
  }

  const code = err && err.code != null ? String(err.code) : '';
  switch (code) {
    case 'auth/invalid-email':
      return 'That email address does not look valid. Please check it and try again.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact us if you need help.';
    case 'auth/too-many-requests':
      return 'Too many sign-in attempts. Please wait a few minutes, then try again.';
    case 'auth/network-request-failed':
      return 'We could not reach the server. Check your internet connection and try again.';
    default: {
      const raw = extractApiErrorMessage(err);
      if (raw && !raw.trim().startsWith('{') && raw.length < 200) {
        return raw;
      }
      return 'Sign in failed. Please try again.';
    }
  }
}
